import React, { useState, useEffect } from 'react';
import { processService } from '../../services/api';

/**
 * Code Analysis Form Component
 * Allows user to select process and files for UiPath code analysis within chat
 */
const CodeAnalysisForm = ({ projectId, onSubmit, onCancel, onStop = null, isAnalyzing = false }) => {
  const [processes, setProcesses] = useState([]);
  const [selectedProcess, setSelectedProcess] = useState('');
  const [selectedScope, setSelectedScope] = useState('full-process');
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(true);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [error, setError] = useState(null);
  const [fileFilter, setFileFilter] = useState('');
  const [analysisMode, setAnalysisMode] = useState('comprehensive'); // 'standard' or 'comprehensive'

  // Load processes on mount
  useEffect(() => {
    loadProcesses();
  }, [projectId]);

  // Load files when process is selected
  useEffect(() => {
    if (selectedProcess && selectedScope === 'specific-files') {
      loadFilesForProcess(selectedProcess);
    } else {
      setFiles([]);
      setSelectedFiles([]);
    }
  }, [selectedProcess, selectedScope]);

  const loadProcesses = async () => {
    setIsLoadingProcesses(true);
    setError(null);
    
    try {
      const response = await processService.getProcessesByProject(projectId);
      setProcesses(response.data || []);
      
      // Auto-select first process if available
      if (response.data && response.data.length > 0) {
        setSelectedProcess(response.data[0].id);
      }
    } catch (err) {
      console.error('Error loading processes:', err);
      setError('Failed to load processes');
    } finally {
      setIsLoadingProcesses(false);
    }
  };

  const loadFilesForProcess = async (processId) => {
    setIsLoadingFiles(true);
    setError(null);
    
    try {
      // Get files from uipath_code for this process
      const response = await processService.getProcessFiles(processId);
      setFiles(response.data || []);
    } catch (err) {
      console.error('Error loading files:', err);
      setError('Failed to load files for process');
      setFiles([]);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prepare analysis request
    const analysisRequest = {
      projectId,
      processId: selectedProcess || null,
      scope: selectedScope,
      files: selectedScope === 'specific-files' ? selectedFiles : [],
      analysisMode // 'standard' or 'comprehensive'
    };

    onSubmit(analysisRequest);
  };

  const handleFileToggle = (filePath) => {
    setSelectedFiles(prev => {
      if (prev.includes(filePath)) {
        return prev.filter(f => f !== filePath);
      } else {
        return [...prev, filePath];
      }
    });
  };

  const handleSelectAllFiles = () => {
    setSelectedFiles(files.map(f => f.filePath));
  };

  const handleDeselectAllFiles = () => {
    setSelectedFiles([]);
  };

  // Filter files based on search text
  const filteredFiles = files.filter(file => 
    file.filePath.toLowerCase().includes(fileFilter.toLowerCase())
  );

  if (isLoadingProcesses) {
    return (
      <div className="rounded-lg p-4 mb-3" style={{background: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
        <div className="flex items-center justify-center py-4">
          <div style={{color: 'rgba(255,255,255,0.5)'}}>Loading processes...</div>
        </div>
      </div>
    );
  }

  if (processes.length === 0) {
    return (
      <div className="rounded-lg p-4 mb-3" style={{background: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
        <div className="flex items-center gap-2 mb-2">
          <span className="font-medium" style={{color: 'rgba(255,255,255,0.75)'}}>⚠️ No Processes Found</span>
        </div>
        <p className="text-sm mb-3" style={{color: 'rgba(255,255,255,0.5)'}}>
          This project has no processes uploaded yet. Please upload a UiPath process first.
        </p>
        <button
          onClick={onCancel}
          className="btn-secondary text-sm px-3 py-1.5 rounded-md"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-lg p-4 mb-3 shadow-sm" style={{background: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
      <form onSubmit={handleSubmit} className="flex flex-col max-h-[calc(100vh-200px)]">
        {/* Scrollable content area */}
        <div className="overflow-y-auto pr-1">
          {/* Header */}
          <div className="mb-4 pb-3" style={{borderBottom: '1px solid var(--tqa-border-dark)'}}>
            <h3 className="text-base font-semibold flex items-center gap-2" style={{color: 'rgba(255,255,255,0.85)'}}>
              🔍 Code Analysis Settings
            </h3>
            <p className="text-xs mt-1" style={{color: 'rgba(255,255,255,0.5)'}}>
              Configure and run code analysis on your UiPath process
            </p>
          </div>

          {error && (
            <div className="mb-3 p-2 rounded text-xs" style={{background: 'rgba(230,100,97,0.1)', border: '1px solid rgba(230,100,97,0.3)', color: '#f87171'}}>
              {error}
            </div>
          )}

          {/* Process Selection */}
          <div className="mb-3">
            <label className="block text-xs font-medium mb-1.5" style={{color: 'rgba(255,255,255,0.65)'}}>
              Select Process
            </label>
            <select
              value={selectedProcess}
              onChange={(e) => setSelectedProcess(e.target.value)}
              className="w-full rounded-md px-3 py-2 focus:outline-none text-sm"
              style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
              required
            >
              <option value="">-- Select a Process --</option>
              {processes.map(process => (
                <option key={process.id} value={process.id}>
                  {process.name} {process.technicalName ? `(${process.technicalName})` : ''} 
                  {process.version ? ` v${process.version}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Analysis Mode Selection */}
          <div className="mb-3">
            <label className="text-xs font-medium block mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>
              Analysis Mode
            </label>
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium transition-colors" style={{color: analysisMode === 'standard' ? 'var(--tqa-green)' : 'rgba(255,255,255,0.5)'}}>
                Standard
              </span>
              <button
                type="button"
                onClick={() => setAnalysisMode(analysisMode === 'standard' ? 'comprehensive' : 'standard')}
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
                style={{background: analysisMode === 'comprehensive' ? 'var(--tqa-green)' : 'rgba(255,255,255,0.2)'}}
                title={analysisMode === 'standard' ? 'Switch to comprehensive analysis' : 'Switch to standard analysis'}
              >
                <span className="sr-only">Toggle analysis mode</span>
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full shadow ring-0 transition duration-200 ease-in-out ${
                    analysisMode === 'comprehensive' ? 'translate-x-4' : 'translate-x-0'
                  }`}
                  style={{backgroundColor: 'var(--tqa-bg)'}}
                />
              </button>
              <span className="text-xs font-medium transition-colors" style={{color: analysisMode === 'comprehensive' ? 'var(--tqa-green)' : 'rgba(255,255,255,0.5)'}}>
                Comprehensive
              </span>
            </div>
            <p className="text-xs mt-1.5" style={{color: 'rgba(255,255,255,0.4)'}}>
              {analysisMode === 'standard' 
                ? '⚡ Fast single-pass analysis' 
                : '🔬 Dual-reviewer analysis with cross-verification (slower, more thorough)'}
            </p>
          </div>

          {/* Scope Selection - Compact Toggle */}
          <div className="mb-3">
            <label className="text-xs font-medium block mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>
              Analysis Scope
            </label>
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium transition-colors" style={{color: selectedScope === 'full-process' ? 'var(--tqa-green)' : 'rgba(255,255,255,0.5)'}}>
                All files
              </span>
              <button
                type="button"
                onClick={() => setSelectedScope(selectedScope === 'full-process' ? 'specific-files' : 'full-process')}
                className="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                style={{background: selectedScope === 'specific-files' ? 'var(--tqa-green)' : 'rgba(255,255,255,0.2)'}}
                title={selectedScope === 'full-process' ? 'Switch to specific files' : 'Switch to full process'}
              >
                <span className="sr-only">Toggle analysis scope</span>
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full shadow ring-0 transition duration-200 ease-in-out ${
                    selectedScope === 'specific-files' ? 'translate-x-4' : 'translate-x-0'
                  }`}
                  style={{backgroundColor: 'var(--tqa-bg)'}}
                />
              </button>
              <span className="text-xs font-medium transition-colors" style={{color: selectedScope === 'specific-files' ? 'var(--tqa-green)' : 'rgba(255,255,255,0.5)'}}>
                Specific files
              </span>
            </div>
          </div>


          {/* File Selection (only for specific-files scope) */}
          {selectedScope === 'specific-files' && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium" style={{color: 'rgba(255,255,255,0.65)'}}>
                  Select Files ({selectedFiles.length} selected)
                </label>
                {files.length > 0 && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleSelectAllFiles}
                      className="text-xs font-medium text-tqa-blue hover:text-tqa-navy hover:underline"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleDeselectAllFiles}
                      className="text-xs font-medium hover:underline"
                      style={{color: 'rgba(255,255,255,0.5)'}}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              {/* File Filter Input */}
              {files.length > 0 && (
                <div className="mb-2">
                  <input
                    type="text"
                    value={fileFilter}
                    onChange={(e) => setFileFilter(e.target.value)}
                    placeholder="🔍 Filter files..."
                    className="w-full rounded-md px-3 py-1.5 focus:outline-none text-sm"
                    style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
                  />
                  {fileFilter && (
                    <div className="mt-1 text-xs" style={{color: 'rgba(255,255,255,0.4)'}}>
                      {filteredFiles.length} of {files.length} files
                    </div>
                  )}
                </div>
              )}

              {isLoadingFiles ? (
                <div className="text-xs py-3 text-center rounded-md" style={{color: 'rgba(255,255,255,0.5)', background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)'}}>
                  <div className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full animate-spin mr-2" style={{borderColor: 'var(--tqa-green)', borderTopColor: 'transparent'}}></div>
                  Loading files...
                </div>
              ) : files.length === 0 ? (
                <div className="text-xs py-3 text-center rounded-md px-3" style={{color: 'rgba(255,255,255,0.5)', background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)'}}>
                  ℹ️ No files available for this process.
                </div>
              ) : filteredFiles.length === 0 ? (
                <div className="text-xs py-3 text-center rounded-md px-3" style={{color: 'rgba(255,255,255,0.5)', background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)'}}>
                  🔍 No files match "{fileFilter}"
                </div>
              ) : (
                <div className="rounded-md max-h-40 overflow-y-auto" style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)'}}>
                  {filteredFiles.map((file) => (
                    <label
                      key={file.filePath}
                      className="flex items-center px-3 py-2 cursor-pointer transition-colors last:border-b-0"
                      style={{borderBottom: '1px solid rgba(255,255,255,0.06)'}}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={selectedFiles.includes(file.filePath)}
                        onChange={() => handleFileToggle(file.filePath)}
                        className="mr-2 h-4 w-4 text-tqa-blue rounded focus:ring-2 focus:ring-tqa-blue flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate" style={{color: 'rgba(255,255,255,0.75)'}}>
                          {file.filePath}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {selectedScope === 'specific-files' && selectedFiles.length === 0 && files.length > 0 && (
                <div className="mt-2 text-xs font-medium px-2 py-1.5 rounded" style={{color: '#fbbf24', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)'}}>
                  ⚠️ Please select at least one file
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons - Fixed at bottom */}
        <div className="flex justify-end gap-2 pt-3 mt-2" style={{borderTop: '1px solid var(--tqa-border-dark)'}}>
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary px-4 py-2 text-sm font-medium rounded-md transition-all disabled:opacity-50"
            disabled={isAnalyzing}
          >
            Cancel
          </button>
          <button
            type={isAnalyzing ? 'button' : 'submit'}
            onClick={isAnalyzing ? onStop : undefined}
            className="px-5 py-2 text-sm font-medium text-white bg-tqa-blue rounded-md hover:bg-tqa-navy transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow"
            disabled={
              (!isAnalyzing && (
              !selectedProcess ||
              (selectedScope === 'specific-files' && selectedFiles.length === 0)
              ))
            }
          >
            {isAnalyzing ? (
              'Stop'
            ) : (
              '🔍 Start Analysis'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};


export default CodeAnalysisForm;
